/* BadgeKey lives in TierBadges (the components do) — a type-only import
   is erased at compile, so the shared data module stays server-safe */
import type { BadgeKey } from "./TierBadges";

export type { BadgeKey };

export type Status =
  | "online"
  | "idle"
  | "dnd"
  | "sleeping"
  | "work"
  | "offline";

export const STATUS_TEXT: Record<Status, { label: string; color: string }> = {
  online: { label: "Online", color: "text-online" },
  idle: { label: "Idle", color: "text-idle" },
  dnd: { label: "Do Not Disturb", color: "text-dnd" },
  sleeping: { label: "Sleeping", color: "text-sleepy" },
  work: { label: "Working", color: "text-work" },
  offline: { label: "Offline", color: "text-fg-faint" },
};



export interface Member {
  id: string;
  name: string;
  tag: string;
  src: string;
  status: Status;
  role?: string;
  /* GC role tier — drives the right-side row icon.
     owner 1/1* (required, exactly one) · followup 1/1 (no icon)
     mod 3/5 · perms 3/25 */
  rank?: "owner" | "followup" | "mod" | "perms";
  /* extra badges — joinday + first100 are implied for everyone */
  badges?: BadgeKey[];
  joined: string;
  /* quick-card extras — bio, mutual friends (member ids), mutual
     servers (icon + name), and whether they're already your friend */
  bio?: string;
  mutFriends?: string[];
  mutServers?: { name: string; src: string }[];
  friended?: boolean;
  /* profile extras — a private note you set on them, and the friend
     anniversary (friended members only) */
  note?: string;
  friendsSince?: string;
  /* the client account — hides message/friend actions on its profile */
  self?: boolean;
}

/* deterministic fake snowflake from the member id — the uuid half of
   share links and "Stats for nerds" */
export function memberUuid(m: Member): string {
  let h = 0x2d2c1f5e;
  for (const c of m.id) h = (h * 0x01000193 + c.charCodeAt(0)) >>> 0;
  return String(10_000_000_000_000_000_0 + h * 97_991).slice(0, 18);
}

/* resolves a /users/<key> share link — `<uuid>:<tag>` matches the uuid
   directly, `<username>:<tag>` does the name lookup; both halves must
   pair with the same member */
export function memberFromKey(key: string): Member | undefined {
  const [head, tag] = key.split(":");
  if (!head || !tag) return undefined;
  return MEMBERS.find(
    (x) => x.tag === tag && (memberUuid(x) === head || x.id === head),
  );
}

/* the GC/notes roster — cataas cat pfps pulled into /public */
export const MEMBERS: Member[] = [
  { id: "milk", name: "Milk", tag: "0001", src: "/cat1.jpg", status: "online", role: "Owner", rank: "owner", badges: ["owner", "goaded", "goldbug"], joined: "Jan 4, 2021", bio: "founder energy only", mutFriends: ["gravel", "bean", "luna"], mutServers: [{ name: "The GC", src: "/gc.jpg" }, { name: "Iso Dev", src: "/dm1.jpg" }], friended: true, friendsSince: "Jun 2022" },
  { id: "gravel", name: "Gravel", tag: "4832", src: "/cat2.jpg", status: "idle", role: "Mod", rank: "mod", badges: ["mod", "bug"], joined: "Feb 19, 2021", bio: "reads every diff, twice", mutFriends: ["milk", "mochi"], mutServers: [{ name: "Iso Dev", src: "/dm1.jpg" }], friended: true, friendsSince: "Jun 2022" },
  { id: "bean", name: "Bean", tag: "7100", src: "/cat3.jpg", status: "dnd", role: "Co-Owner", rank: "followup", badges: ["roadsign"], joined: "Jan 4, 2021", bio: "asleep at the keyboard", mutFriends: ["milk"], mutServers: [{ name: "The GC", src: "/gc.jpg" }, { name: "Notes", src: "/notes.jpg" }], friended: true, friendsSince: "Jun 2022" },
  { id: "mochi", name: "Mochi", tag: "2049", src: "/cat4.jpg", status: "online", rank: "perms", badges: ["bug"], joined: "Apr 2, 2021", mutFriends: ["gravel"], mutServers: [{ name: "Iso Dev", src: "/dm1.jpg" }] },
  { id: "socks", name: "Socks", tag: "5510", src: "/cat5.jpg", status: "offline", joined: "May 27, 2021", bio: "will reply in 3-5 business days", mutServers: [{ name: "Notes", src: "/notes.jpg" }] },
  { id: "luna", name: "Luna", tag: "0019", src: "/cat6.jpg", status: "online", role: "Mod", rank: "mod", badges: ["sr-mod"], joined: "Mar 8, 2021", bio: "night shift gremlin. i review code at 3am and regret it at 9am. cat mom of three, plant mom of zero (they all died). if the deploy is green i am nowhere to be found.", mutFriends: ["milk", "bean", "ziggy", "biscuit", "pepper"], mutServers: [{ name: "Iso Dev", src: "/dm1.jpg" }, { name: "The GC", src: "/gc.jpg" }], friended: true, friendsSince: "Jun 2022" },
  { id: "noodle", name: "Noodle", tag: "8341", src: "/cat7.jpg", status: "idle", rank: "perms", badges: ["jr-designer"], joined: "Jun 14, 2021", mutFriends: ["pepper"] },
  { id: "ziggy", name: "Ziggy", tag: "1207", src: "/cat8.jpg", status: "offline", badges: ["bug"], joined: "Jul 30, 2021", bio: "ships on fridays. yes i know you shouldnt. yes i do it anyway. professional risk-taker, unprofessional sleeper. ask me about the incident of june 13th and watch me change the subject", mutFriends: ["luna", "pepper"], mutServers: [{ name: "Iso Dev", src: "/dm1.jpg" }, { name: "Notes", src: "/notes.jpg" }] },
  { id: "pepper", name: "Pepper", tag: "6683", src: "/cat9.jpg", status: "dnd", rank: "perms", badges: ["dev"], joined: "Aug 11, 2021", mutFriends: ["noodle", "ziggy"], mutServers: [{ name: "The GC", src: "/gc.jpg" }] },
  { id: "biscuit", name: "Biscuit", tag: "3945", src: "/cat10.jpg", status: "work", role: "Mod", rank: "mod", badges: ["jr-mod"], joined: "Sep 23, 2021", bio: "crumbs in the codebase", mutFriends: ["luna"], mutServers: [{ name: "Iso Dev", src: "/dm1.jpg" }], friended: true, friendsSince: "Jun 2022" },
];

export interface Person {
  id: string;
  name: string;
  initials: string;
  role: string;
  color: string;
  status: Status;
}

export interface Message {
  id: string;
  authorId: string;
  time: string;
  text: string;
}

export interface Conversation {
  id: string;
  title: string;
  subtitle: string;
  initials: string;
  color: string;
  status: Status;
  preview: string;
  time: string;
  unread: number;
  messages: Message[];
}

/* dm-bar + share-sheet rows — person dms (/app/d/), group chats
   (/app/g/), and the notes channel (/app/n/). `hover` is the row's
   preview-swap text (username for dms, file for notes), `from`/`msg`
   the last-message preview, `note` a notes last-update line */
export interface DmEntry {
  id: string;
  name: string;
  src: string;
  href: string;
  status?: Status;
  from?: string;
  msg?: string;
  note?: string;
  hover?: string;
  unread?: boolean;
}

/* the one server — the rail tile routes to /s/<id>, which lands on the
   first channel (/s/<id>/<channel>) */
export interface ServerChannel {
  id: string;
  name: string;
  topic: string;
}

export const SERVER = {
  id: "1023456789012345678",
  name: "SRVR",
  src: "/favicon.webp",
  channels: [
    { id: "201234567890123456", name: "general", topic: "the only channel that matters" },
    { id: "202345678901234567", name: "dev", topic: "isolation dev chatter" },
    { id: "203456789012345678", name: "showcase", topic: "post your wins" },
    { id: "204567890123456789", name: "random", topic: "no topic. never had one" },
  ] as ServerChannel[],
};

/* the channel a /s/<server>/<channel> path points at — undefined on the
   server root or a different server */
export function channelAt(pathname: string): ServerChannel | undefined {
  const seg = pathname.split("/");
  if (seg[1] !== "s" || seg[2] !== SERVER.id) return undefined;
  return SERVER.channels.find((c) => c.id === seg[3]);
}

export const DMS: DmEntry[] = [
  {
    id: "milk",
    name: "Milk",
    src: "/dm1.jpg",
    href: "/app/d/milk",
    status: "online",
    from: "milk",
    msg: "new build drops tonight btw",
    hover: "milk#0001",
    unread: true,
  },
  {
    id: "gravel",
    name: "Gravel",
    src: "/dm2.jpg",
    href: "/app/d/gravel",
    status: "idle",
    from: "you",
    msg: "lmao send it over",
    hover: "gravel#4832",
    unread: true,
  },
  {
    id: "gc",
    name: "The GC",
    src: "/gc.jpg",
    href: "/app/g/1034567890123456789",
    from: "pepper",
    msg: "movie night saturday?",
    unread: true,
  },
  {
    id: "notes",
    name: "Notes",
    src: "/notes.jpg",
    href: "/app/n/1045678901234567890",
    note: "edited 2m ago",
    hover: "vault.md",
  },
];


/* ── messages ──────────────────────────────────────────────────── */

export interface Reaction {
  e: string; /* the emoji char — same sheet the picker uses */
  n: number; /* count */
  me?: boolean; /* whether the local account reacted */
}

/* components-v2-style embed — built by the composer's embed form.
   every field optional; empty segments render nothing */
export interface EmbedData {
  /* left accent-bar color (hex) — "tone" on the header */
  tone?: string;
  /* small text above the header + its left icon */
  subHeader?: string;
  subIcon?: string;
  /* the header line + its left icon */
  header?: string;
  icon?: string;
  /* body copy + optional left icon */
  body?: string;
  bodyIcon?: string;
  /* footer line + its left icon */
  footer?: string;
  footerIcon?: string;
}

export interface ChatMsg {
  id: string;
  /* a member id, or "you" for the local account */
  author: string;
  /* rendered timestamp label — "12:34 PM"-style, or "Today"-ish day
     markers land between messages via the divider */
  t: string;
  text: string;
  /* structured card under the message body, built by the embed form */
  embed?: EmbedData;
  reactions?: Reaction[];
  /* set when the message was edited after send — shows "(edited)" */
  edited?: boolean;
  /* id of the message this replies to — renders the quote spine
     above the body and breaks same-author grouping */
  replyTo?: string;
  /* pinned messages surface in the header's pins popup */
  pinned?: boolean;
  /* uploaded files — src is an object URL when the file is previewable;
     kind drives the renderer (image/audio/video/file chip). .webm is
     ambiguous — voice notes AND camera video — so kind is explicit */
  attachments?: {
    name: string;
    src?: string;
    kind?: "image" | "audio" | "video" | "file";
  }[];
}

/* thread per surface — keyed by dm member id, server channel id, or
   the fixed "gc"/"notes" keys. Seed convos exercise the markdown-lite
   renderer (bold/italic/code/strike/mentions) */
export const THREADS: Record<string, ChatMsg[]> = {
  gc: [
    { id: "g1", author: "gravel", t: "9:14 AM", text: "morning. who broke prod" },
    { id: "g2", author: "ziggy", t: "9:15 AM", text: "**it wasnt me** this time" },
    { id: "g3", author: "ziggy", t: "9:15 AM", text: "ok it mightve been me" },
    { id: "g4", author: "milk", t: "9:16 AM", text: "ziggy. `git blame` says otherwise", reactions: [{ e: "😂", n: 4 }, { e: "🔥", n: 1, me: true }] },
    { id: "g5", author: "luna", t: "9:22 AM", text: "deploy is green now, everyone breathe" },
    { id: "g6", author: "luna", t: "9:22 AM", text: "*mostly green*" },
    { id: "g7", author: "bean", t: "10:05 AM", text: "just woke up. did i miss the fire", replyTo: "g4" },
    { id: "g8", author: "milk", t: "10:06 AM", text: "you missed **the** fire, the sequel, and the postmortem" },
    { id: "g9", author: "noodle", t: "11:40 AM", text: "new badge designs are up in #showcase btw", reactions: [{ e: "🎨", n: 2 }, { e: "❤️", n: 3 }] },
    { id: "g10", author: "mochi", t: "12:02 PM", text: "they look so clean?? the impeccable gradient moves" },
    { id: "g11", author: "pepper", t: "12:15 PM", text: "hot take: `pnpm` supremacy. fight me @ziggy" },
    { id: "g12", author: "ziggy", t: "12:15 PM", text: "~~fight~~ fine, youre right, but only this once" },
    { id: "g13", author: "you", t: "12:40 PM", text: "chat frame test — __read the room__" },
  ],
  milk: [
    { id: "m1", author: "milk", t: "8:02 PM", text: "new build drops tonight btw" },
    { id: "m2", author: "you", t: "8:03 PM", text: "ship it. what changed" },
    { id: "m3", author: "milk", t: "8:04 PM", text: "badge system is fully live. *your* idea, remember", reactions: [{ e: "👏", n: 1 }] },
    { id: "m4", author: "milk", t: "8:04 PM", text: "also `super-icey` looks absurd, in a good way" },
    { id: "m5", author: "you", t: "8:09 PM", text: "put it in the showcase. **deserved**" },
    { id: "m6", author: "milk", t: "8:11 PM", text: "already did. go check" },
  ],
  gravel: [
    { id: "v1", author: "gravel", t: "7:12 PM", text: "yo, that flag icon in the topbar actually works now" },
    { id: "v2", author: "you", t: "7:13 PM", text: "yeah it opens the full report flow. 4 steps" },
    { id: "v3", author: "gravel", t: "7:15 PM", text: "the TOS checkboxes are a nice touch lol" },
  ],
  /* server channels — keyed by channel id from SERVER.channels */
  "201234567890123456": [
    { id: "s1", author: "milk", t: "9:00 AM", text: "welcome to **#general** — the only channel that matters" },
    { id: "s2", author: "luna", t: "9:30 AM", text: "pinned the release notes in `Pins`", pinned: true },
    { id: "s3", author: "bean", t: "10:15 AM", text: "gm" },
    { id: "s4", author: "bean", t: "10:15 AM", text: "(i am not a morning person, this is a cry for help)" },
    { id: "s5", author: "gravel", t: "11:05 AM", text: "if anyone sees a ~500 error~ that is ~~my fault~~ a mystery" },
    { id: "s6", author: "you", t: "11:40 AM", text: "can confirm, it was a mystery" },
  ],
  "202345678901234567": [
    { id: "d1", author: "ziggy", t: "1:02 PM", text: "isolation dev chatter goes here" },
    { id: "d2", author: "ziggy", t: "1:03 PM", text: "```\nconst ship = () => everyone.panic()\n```" },
    { id: "d3", author: "pepper", t: "1:15 PM", text: "accurate. merging anyway" },
    { id: "d4", author: "biscuit", t: "2:40 PM", text: "crumbs in the codebase, literally" },
  ],
  "203456789012345678": [
    { id: "w1", author: "noodle", t: "11:41 AM", text: "badge ladder mockups — junior → super for every role" },
    { id: "w2", author: "noodle", t: "11:42 AM", text: "the *impeccable* gradient is my entire personality now", reactions: [{ e: "😂", n: 5 }] },
    { id: "w3", author: "mochi", t: "12:03 PM", text: "the swords one for mods is so good" },
  ],
  "204567890123456789": [
    { id: "r1", author: "socks", t: "3:00 AM", text: "is anyone else awake" },
    { id: "r2", author: "socks", t: "3:00 AM", text: "anyone" },
    { id: "r3", author: "luna", t: "3:12 AM", text: "unfortunately" },
  ],
  notes: [
    { id: "n1", author: "you", t: "yesterday", text: "todo: finish the chat frame — **finally**" },
    { id: "n2", author: "you", t: "yesterday", text: "markup renderer: `inline` + fenced blocks. keep it small" },
    { id: "n3", author: "you", t: "9:00 AM", text: "badge laddER DONE. chat frame next" },
  ],
};
